import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Trophy, CaretUp, CaretDown } from "@phosphor-icons/react";
import { toast } from "sonner";

const fmtINR = (n) => {
  const v = Number(n || 0);
  if (v >= 1e7) return `₹${(v / 1e7).toFixed(2)} Cr`;
  if (v >= 1e5) return `₹${(v / 1e5).toFixed(2)} L`;
  return `₹${v.toLocaleString("en-IN")}`;
};

const RANGES = [
  { key: "week",  label: "This week",  days: 7 },
  { key: "month", label: "This month", days: 30 },
  { key: "qtr",   label: "This qtr",   days: 90 },
  { key: "year",  label: "Year to date", days: 365 },
];

const COLS = [
  { key: "name",                    label: "Teammate",       kind: "text" },
  { key: "deals_owned",             label: "Open deals",     kind: "int" },
  { key: "open_pipeline_value",     label: "Open pipeline",  kind: "money" },
  { key: "closed_won_value",        label: "Closed-won",     kind: "money" },
  { key: "closed_won_count",        label: "Wins",           kind: "int" },
  { key: "avg_cycle_days",          label: "Avg cycle (d)",  kind: "float" },
  { key: "notes_this_period",       label: "Notes logged",   kind: "int" },
  { key: "deals_moved_this_period", label: "Deals moved",    kind: "int" },
];

export default function TeamLeaderboardCard() {
  const nav = useNavigate();
  const [range, setRange] = useState("month");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sortCol, setSortCol] = useState("closed_won_value");
  const [sortDir, setSortDir] = useState("desc");

  const load = async (rangeKey) => {
    setBusy(true);
    const days = RANGES.find(r => r.key === rangeKey)?.days || 30;
    const end = new Date();
    const start = new Date(); start.setDate(end.getDate() - days);
    try {
      const { data } = await api.get("/team/leaderboard", { params: {
        start_date: start.toISOString().slice(0, 10),
        end_date:   end.toISOString().slice(0, 10),
      }});
      setData(data);
    } catch (e) { toast.error("Team leaderboard failed to load"); }
    finally { setBusy(false); }
  };

  useEffect(() => { load(range); }, [range]);

  const rows = useMemo(() => {
    const arr = [...((data && data.rows) || [])];
    const col = COLS.find(c => c.key === sortCol);
    if (!col) return arr;
    arr.sort((a, b) => {
      const va = a[sortCol]; const vb = b[sortCol];
      if (col.kind === "text") return String(va || "").localeCompare(String(vb || "")) * (sortDir === "asc" ? 1 : -1);
      return ((Number(va) || 0) - (Number(vb) || 0)) * (sortDir === "asc" ? 1 : -1);
    });
    return arr;
  }, [data, sortCol, sortDir]);

  const flip = (key) => {
    if (sortCol === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortCol(key); setSortDir(key === "name" ? "asc" : "desc"); }
  };

  return (
    <section className="bg-white border border-slate-200 rounded-md p-5" data-testid="team-leaderboard">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-md bg-amber-100 grid place-items-center"><Trophy size={18} className="text-amber-600" weight="fill"/></div>
          <div>
            <div className="label-micro">Sales · individual effort</div>
            <h2 className="font-display text-lg font-black text-slate-900">Team leaderboard</h2>
          </div>
        </div>
        <div className="inline-flex border border-slate-200 rounded-sm overflow-hidden" data-testid="leaderboard-range">
          {RANGES.map(r => (
            <button key={r.key} onClick={() => setRange(r.key)}
                    data-testid={`leaderboard-range-${r.key}`}
                    className={`text-xs font-semibold px-3 py-1.5 transition-colors ${range === r.key ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {busy && !data && <div className="text-xs text-slate-500 italic text-center py-6">Loading leaderboard…</div>}
      {!busy && rows.length === 0 && <div className="text-xs text-slate-500 italic text-center py-6">No teammate activity in this range yet.</div>}
      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="leaderboard-table">
            <thead>
              <tr className="text-left border-b border-slate-200">
                {COLS.map(c => (
                  <th key={c.key} className="py-2 px-2 label-micro select-none cursor-pointer hover:bg-slate-50" onClick={() => flip(c.key)}
                      data-testid={`leaderboard-col-${c.key}`}>
                    <span className="inline-flex items-center gap-1">{c.label}
                      {sortCol === c.key && (sortDir === "asc" ? <CaretUp size={10} weight="bold"/> : <CaretDown size={10} weight="bold"/>)}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.user_id || "unassigned"} onClick={() => nav(`/team/${r.user_id || "unassigned"}`)}
                    className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                    data-testid={`leaderboard-row-${r.user_id || "unassigned"}`}>
                  <td className="py-2.5 px-2">
                    <div className="flex items-center gap-2">
                      {i === 0 && r.closed_won_count > 0 && <span title="Top of the leaderboard">🏆</span>}
                      <span className="w-7 h-7 rounded-full bg-slate-800 text-white grid place-items-center text-[10px] font-bold shrink-0">{(r.name || "?").slice(0,1).toUpperCase()}</span>
                      <div className="min-w-0">
                        <div className="font-semibold text-slate-900 truncate">{r.name}</div>
                        {r.email && <div className="text-[10px] text-slate-500 truncate">{r.email}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="py-2.5 px-2 tabular-nums">{r.deals_owned}</td>
                  <td className="py-2.5 px-2 tabular-nums font-semibold">{fmtINR(r.open_pipeline_value)}</td>
                  <td className="py-2.5 px-2 tabular-nums font-black text-emerald-700">{fmtINR(r.closed_won_value)}</td>
                  <td className="py-2.5 px-2 tabular-nums">{r.closed_won_count}</td>
                  <td className="py-2.5 px-2 tabular-nums">{r.avg_cycle_days || "—"}</td>
                  <td className="py-2.5 px-2 tabular-nums">{r.notes_this_period}</td>
                  <td className="py-2.5 px-2 tabular-nums">{r.deals_moved_this_period}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
